// "Why we exist" motif: the MindFeed mark with three satellites — yellow
// (people), blue (business), green (agents) — orbiting on rings at different
// speeds, above a small legend. Purely decorative.
//
// Each ring carries one satellite pinned to its edge and spins via `orb-spin`;
// the ring's base transform is translate(-50%,-50%) so the keyframe preserves
// centring while rotating. Durations/directions vary per ring for a calm,
// non-synchronised drift. Disabled under prefers-reduced-motion.
import { useTranslations } from 'next-intl';

const MindFeedMark = () => (
  <svg
    viewBox="0 0 331 199"
    fill="none"
    className={styles.logo}
    aria-hidden="true"
  >
    <path
      d="M59.8043 1.11679C55.5903 2.30942 53.6821 3.58156 53.6821 5.17174C53.6821 5.80781 56.5445 11.3734 60.0428 17.4956C63.5412 23.6973 66.4036 28.8654 66.4036 29.104C66.4036 29.263 68.7093 33.3974 71.4921 38.327C74.2749 43.2565 80.4766 54.3878 85.2472 63.1338C90.0972 71.8798 98.5252 87.066 103.932 96.9251C109.418 106.784 118.164 122.686 123.491 132.307C146.867 175.082 149.093 178.342 160.463 186.77C172.23 195.596 183.441 198.856 201.569 198.935C213.495 199.015 218.504 197.981 219.538 195.198C219.776 194.562 214.767 184.544 208.089 172.3C201.569 160.373 192.425 143.438 187.655 134.692C182.964 125.946 176.285 113.781 172.946 107.659C169.527 101.537 165.074 93.2677 163.007 89.3717C160.94 85.3963 155.215 74.9011 150.286 65.9166C145.435 56.9321 139.552 46.0394 137.167 41.6664C130.726 29.422 126.592 23.6973 119.198 16.7005C106.953 5.09223 92.4825 -0.155359 73.1618 0.00365876C67.9142 0.00365876 61.8716 0.560223 59.8043 1.11679Z"
      fill="currentColor"
    />
    <path
      d="M174.919 0.401015C169.591 1.59365 166.491 2.62727 165.377 3.89941C164.026 5.41008 165.139 7.79536 178.099 30.5349C180.802 35.3055 183.903 40.8711 185.016 42.8588C188.912 49.9351 201.077 72.1181 206.325 81.4206C209.266 86.6682 212.844 93.1085 214.275 95.7323C215.707 98.356 218.728 103.922 221.034 108.056C223.339 112.191 227.474 119.744 230.177 124.753C232.96 129.762 236.856 136.918 238.923 140.655C240.99 144.392 244.012 149.878 245.681 152.979C252.599 165.859 254.427 169.119 257.131 172.856C265.956 185.339 279.393 194.164 294.739 197.583C301.974 199.253 321.295 199.491 326.145 198.06C329.722 197.027 331.79 194.482 330.518 192.892C329.881 192.097 326.542 186.054 312.867 160.93C311.674 158.703 307.221 150.514 302.928 142.643C298.714 134.771 293.069 124.514 290.525 119.744C287.901 115.053 283.607 107.182 280.983 102.252C278.28 97.4019 275.02 91.3593 273.669 88.974C272.317 86.5887 269.216 80.8641 266.751 76.2526C264.287 71.641 260.232 64.3262 257.846 59.9532C251.963 49.2195 247.908 41.7457 245.045 36.4981C236.538 20.8348 225.009 9.78308 211.572 4.615C203.701 1.59366 194.08 -0.0760325 185.096 0.00347646C180.325 0.0829854 175.793 0.241997 174.919 0.401015Z"
      fill="currentColor"
    />
    <path
      d="M26.9445 127.059C21.8559 128.331 14.064 132.784 10.4066 136.441C2.93276 143.835 -1.20171 156.318 0.308961 166.893C3.3303 187.883 24.6387 201.718 44.5159 195.675C63.7571 189.792 74.4113 170.471 68.7662 151.707C65.5063 141.053 59.6226 134.374 48.8889 129.285C44.675 127.297 42.6872 126.9 36.565 126.661C32.5896 126.502 28.2961 126.661 26.9445 127.059Z"
      fill="currentColor"
    />
  </svg>
);

const LEGEND = [
  { key: 'people', dot: 'bg-[#FDE47A]' },
  { key: 'companies', dot: 'bg-[#7fa8ff]' },
  { key: 'aiAgents', dot: 'bg-[#6ed7a6]' },
];

export const KnowledgeOrbit = () => {
  const t = useTranslations();
  return (
    <div className={styles.band} aria-hidden="true">
      <div className={styles.orbit}>
        <span className={styles.glow} />
        <MindFeedMark />
        {/* r1 — yellow satellite at the ring's top, 11s forward */}
        <span
          className={`${styles.ring} h-[140px] w-[140px] motion-safe:animate-orb-spin`}
        >
          <span
            className={`${styles.sat} bg-[#FDE47A] shadow-[0_0_10px_rgba(247,203,45,0.85)]`}
          />
        </span>
        {/* r2 — blue satellite, 8s reversed */}
        <span
          className={`${styles.ring} h-[104px] w-[104px] motion-safe:animate-orb-spin-reverse`}
        >
          <span
            className={`${styles.sat} bg-[#7fa8ff] shadow-[0_0_10px_rgba(127,168,255,0.9)]`}
          />
        </span>
        {/* r3 — green satellite at the ring's bottom, 15s forward (slowest) */}
        <span
          className={`${styles.ring} h-[140px] w-[140px] motion-safe:animate-orb-spin-slow`}
        >
          <span
            className={`${styles.satBottom} bg-[#6ed7a6] shadow-[0_0_10px_rgba(110,215,166,0.9)]`}
          />
        </span>
      </div>

      <div className={styles.legend}>
        {LEGEND.map((item) => (
          <span key={item.key} className={styles.legendItem}>
            <i className={`${styles.legendDot} ${item.dot}`} />
            {t(`pages.about.why.orbit.${item.key}`)}
          </span>
        ))}
      </div>
    </div>
  );
};

const styles = {
  band: 'relative flex h-[200px] flex-col items-center justify-center gap-3.5',
  orbit: 'relative h-[140px] w-[140px]',
  glow: [
    'pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full',
    '[background:radial-gradient(circle,rgba(189,222,255,0.6),transparent_66%)]',
    'marketing-dark:[background:radial-gradient(circle,rgba(127,168,255,0.26),transparent_66%)]',
  ].join(' '),
  // `currentColor` mark: ink on the light page, white on navy.
  logo: [
    'absolute left-1/2 top-1/2 z-[2] h-[34px] w-[34px] -translate-x-1/2 -translate-y-1/2',
    'text-mf-off-black-600 [filter:drop-shadow(0_3px_9px_rgba(20,30,60,0.2))]',
    'marketing-dark:text-white marketing-dark:[filter:drop-shadow(0_3px_9px_rgba(0,0,0,0.45))]',
  ].join(' '),
  ring: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-black/[0.08] marketing-dark:border-white/[0.07]',
  sat: 'absolute -top-[4.5px] left-1/2 -ml-[4.5px] h-[9px] w-[9px] rounded-full',
  satBottom:
    'absolute -bottom-[4.5px] left-1/2 -ml-[4.5px] h-[9px] w-[9px] rounded-full',
  legend: 'flex items-center gap-[18px] opacity-55',
  legendItem:
    'inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-mf-gray-500 marketing-dark:text-white/50',
  legendDot: 'h-[5px] w-[5px] rounded-full',
};
